import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import OrderListing from "./order-listing";
import { useStores } from "../../stores/root-store";
import {
  OrderStatus,
  PaymentMethod,
  PaymentStatus,
} from "../../stores/index.d";
import { useAuth } from "../../contexts/auth-context";
import Paginate from "../../shared/pagination/paginate";
import { IOrder } from "../../stores/order";
import { useOrderPaymentPolling } from "../../hooks/use-order-payment-polling";
import { creatHubConnection, isPaymentFailedOrder } from "../../utils/functions";
import { ApiConfig } from "../../services/api-config";
import useSound from "use-sound";
import dingSound from "../../resources/sounds/ding.mp3";
import OrderReceipt from "./order-receipt";
import html2canvas from "html2canvas";
import { AppConfig } from "../../AppConfig";
import { KitchenPrintPolicy, kitchenVersion } from "../../services/kitchen-print-policy";
import {
  getAutomaticPrintingSetupIssue,
  printReceiptImage,
} from "../../services/order-printer";

const itemsPerPage = 5;

const ManageOrders = observer(() => {
  const { isAuthenticated, isShopAdmin, isShopUser } = useAuth();
  const [selectedTab, setSelectedTab] = useState(1);
  const { orderStore } = useStores();
  const {
    orders,
    fetchOrders,
    receiveNewOrders,
    updateOrderPrintStatus,
    refreshPaymentStatus,
  } = orderStore;
  const [newOrdersPaginated, setNewOrdersPaginated] = useState<IOrder[]>([]);
  const [inProgOrdersPaginated, setInProgOrdersPaginated] = useState<IOrder[]>(
    []
  );
  const [pastOrdersPaginated, setPastOrdersPaginated] = useState<IOrder[]>([]);
  const [printableOrder, setPrintableOrder] = useState<IOrder>();
  const printableOrderRef = useRef<HTMLDivElement>(null);
  const activePrintOrder = useRef<IOrder>();
  const printQueue = useRef<IOrder[]>([]);
  const queuedOrPrintingOrderVersions = useRef(new Set<string>());
  const printedOrderVersions = useRef(new Set<string>());
  const announcedOrderVersions = useRef(new Set<string>());
  const printTimer = useRef<ReturnType<typeof setTimeout>>();
  const statusRetryTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const [printError, setPrintError] = useState<string | undefined>(
    getAutomaticPrintingSetupIssue()
  );
  const [playSound] = useSound(dingSound);
  const printPolicy = useRef(new KitchenPrintPolicy());
  const [autoPrintEnabled, setAutoPrintEnabled] = useState(true);
  const autoPrintEnabledRef = useRef(true);

  const getPrintVersion = useCallback(
    (order: IOrder) =>
      `${AppConfig.TenantId}:kitchen-v2:${kitchenVersion(order)}`,
    []
  );

  const startNextPrint = useCallback(() => {
    if (!autoPrintEnabledRef.current || activePrintOrder.current) return;

    const nextOrder = printQueue.current.shift();
    if (!nextOrder) return;

    activePrintOrder.current = nextOrder;
    setPrintableOrder(nextOrder);
  }, []);

  const markOrderAsPrinted = useCallback(
    (orderId: number, attempt = 1) => {
      updateOrderPrintStatus(orderId, (success: boolean) => {
        if (success || attempt >= 3) return;

        const timer = setTimeout(
          () => markOrderAsPrinted(orderId, attempt + 1),
          attempt * 1000
        );
        statusRetryTimers.current.push(timer);
      });
    },
    [updateOrderPrintStatus]
  );

  const finishCurrentPrint = useCallback(
    (printed: boolean) => {
      const completedOrder = activePrintOrder.current;
      activePrintOrder.current = undefined;
      setPrintableOrder(undefined);

      if (completedOrder) {
        const printVersion = getPrintVersion(completedOrder);
        queuedOrPrintingOrderVersions.current.delete(printVersion);

        if (printed) {
          // Mark the order only after the selected transport acknowledges it.
          // Windows spooler acceptance is not confirmation of paper delivery.
          printedOrderVersions.current.add(printVersion);
          printPolicy.current.accepted(completedOrder);
          markOrderAsPrinted(completedOrder.orderId);
        }
      }

      startNextPrint();
    },
    [getPrintVersion, markOrderAsPrinted, startNextPrint]
  );

  useEffect(() => {
    if (!printableOrder) return undefined;

    let cancelled = false;
    printTimer.current = setTimeout(async () => {
      if (!printableOrderRef.current || cancelled) return;

      try {
        const canvas = await html2canvas(printableOrderRef.current, {
          scale: 2,
          backgroundColor: "#ffffff",
        });
        const imageData = canvas.toDataURL("image/png").split(",")[1];
        if (cancelled || !autoPrintEnabledRef.current) {
          if (!cancelled) finishCurrentPrint(false);
          return;
        }
        await printReceiptImage(
          imageData,
          `Kitchen Order ${printableOrder.orderId}`,
          getPrintVersion(printableOrder)
        );

        if (!cancelled) {
          setPrintError(undefined);
          finishCurrentPrint(true);
        }
      } catch (error) {
        console.error("Automatic order printing failed", error);
        if (!cancelled) {
          setPrintError(
            error instanceof Error
              ? error.message
              : "Automatic order printing failed."
          );
          finishCurrentPrint(false);
        }
      }
    }, 150);

    return () => {
      cancelled = true;
      if (printTimer.current) clearTimeout(printTimer.current);
    };
  }, [finishCurrentPrint, getPrintVersion, printableOrder]);

  const queueOrderForPrinting = useCallback(
    (order: IOrder) => {
      const isOpenOrder =
        order.orderStatus === OrderStatus.Placed ||
        order.orderStatus === OrderStatus.InProgress;
      const onlinePaymentIsConfirmed =
        order.paymentMethod !== PaymentMethod.Card ||
        order.paymentStatus === PaymentStatus.Paid;
      const printVersion = getPrintVersion(order);
      const needsPrinting = printPolicy.current.shouldPrint(order);
      const alreadyHandled =
        printedOrderVersions.current.has(printVersion) ||
        queuedOrPrintingOrderVersions.current.has(printVersion);

      if (
        !isOpenOrder ||
        !onlinePaymentIsConfirmed ||
        !needsPrinting ||
        !autoPrintEnabledRef.current ||
        alreadyHandled ||
        isPaymentFailedOrder(order)
      )
        return;

      if (!announcedOrderVersions.current.has(printVersion)) {
        announcedOrderVersions.current.add(printVersion);
        playSound();
      }
      queuedOrPrintingOrderVersions.current.add(printVersion);
      printQueue.current.push(order);
      startNextPrint();
    },
    [getPrintVersion, playSound, startNextPrint]
  );

  const orderHubCallback = useCallback(
    (order: IOrder) => {
      if (!order) return;

      // One page-level listener keeps the order list and print queue alive
      // while New, In-Progress, or Past Orders is the selected tab.
      receiveNewOrders(order);
      queueOrderForPrinting(order);
    },
    [queueOrderForPrinting, receiveNewOrders]
  );

  useEffect(() => {
    if (!isAuthenticated || (!isShopAdmin && !isShopUser)) return undefined;

    const connection = creatHubConnection(
      ApiConfig.REST_URIS.order.ordersHub,
      "ReceiveOrder",
      orderHubCallback
    );

    return () => {
      connection?.stop();
    };
  }, [isAuthenticated, isShopAdmin, isShopUser, orderHubCallback]);

  useEffect(() => {
    if (isAuthenticated && (isShopAdmin || isShopUser))
      fetchOrders((loaded: IOrder[]) => printPolicy.current.initialize(loaded));
  }, [fetchOrders, isAuthenticated, isShopAdmin, isShopUser]);

  useOrderPaymentPolling({
    enabled: isAuthenticated && (isShopAdmin || isShopUser),
    orders,
    checkPayment: refreshPaymentStatus,
    onPaid: () => fetchOrders(undefined, true),
  });

  useEffect(() => {
    const refreshWhenAppReturns = () => {
      if (
        document.visibilityState === "visible" &&
        isAuthenticated &&
        (isShopAdmin || isShopUser)
      )
        fetchOrders(undefined, true);
    };

    document.addEventListener("visibilitychange", refreshWhenAppReturns);
    return () =>
      document.removeEventListener("visibilitychange", refreshWhenAppReturns);
  }, [fetchOrders, isAuthenticated, isShopAdmin, isShopUser]);

  useEffect(() => {
    if (!isAuthenticated || (!isShopAdmin && !isShopUser)) return;

    // Retry only arrivals/changes observed after the initial snapshot.
    // Never sweep historical open orders into the kitchen queue on page load.
    orders.forEach(queueOrderForPrinting);
    const retryTimer = window.setInterval(() => orders.forEach(queueOrderForPrinting), 15000);
    return () => window.clearInterval(retryTimer);
  }, [
    isAuthenticated,
    isShopAdmin,
    isShopUser,
    orders,
    queueOrderForPrinting,
  ]);

  useEffect(() => {
    const retryTimers = statusRetryTimers.current;
    return () => {
      if (printTimer.current) clearTimeout(printTimer.current);
      retryTimers.forEach(clearTimeout);
    };
  }, []);

  const orderList = useMemo(() => {
    return orders;
  }, [orders, orders?.length]);

  const newOrders = useMemo(() => {
    return orderList.filter(
      (p) => p.orderStatus == OrderStatus.Placed && !isPaymentFailedOrder(p)
    );
  }, [orderList, orderList?.length]);

  const inProgOrders = useMemo(() => {
    return orderList.filter(
      (p) =>
        !isPaymentFailedOrder(p) &&
        [
          OrderStatus.InProgress,
          OrderStatus.OutForDelivery,
          OrderStatus.ReadyForCollection,
        ].some((s) => s == p.orderStatus)
    );
  }, [orderList, orderList?.length]);

  const pastOrders = useMemo(() => {
    return orderList.filter(
      (p) =>
        isPaymentFailedOrder(p) ||
        [
          OrderStatus.Cancelled,
          OrderStatus.Completed,
          OrderStatus.RefundInitiated,
          OrderStatus.Refunded,
        ].some((s) => s == p.orderStatus)
    );
  }, [orderList, orderList?.length]);

  return (
    <div className="container-fluid ps-lg-5 pe-lg-5 mt-3">
      <div className="col-lg-9 mx-auto mt-3">
        <h5 className="text-start">Orders</h5>
        <div className="alert alert-info py-2 d-flex align-items-center justify-content-between gap-2">
          <span>{autoPrintEnabled ? "Auto-print is on for new arrivals. Existing orders are not printed on page load." : "Auto-print is paused on this page."}</span>
          <button type="button" className="btn btn-sm btn-outline-dark" onClick={() => {
            const enabled = !autoPrintEnabledRef.current;
            autoPrintEnabledRef.current = enabled;
            setAutoPrintEnabled(enabled);
            if (!enabled) {
              printQueue.current = [];
              queuedOrPrintingOrderVersions.current.clear();
            } else orders.forEach(queueOrderForPrinting);
          }}>{autoPrintEnabled ? "Pause auto-print" : "Resume auto-print"}</button>
        </div>
        {printError && (
          <div className="alert alert-danger py-2" role="alert">
            <strong>Kitchen auto-print is not ready.</strong> {printError}
          </div>
        )}
        <ul className="nav nav-tabs nav-fill mb-3" id="addons" role="tablist">
          <li className="nav-item" role="presentation">
            <a
              data-mdb-tab-init
              className={`nav-link ${
                selectedTab === 1 ? "active bg-default text-white" : ""
              }`}
              href="#new"
              role="tab"
              aria-controls="opts-tabs-1"
              aria-selected="true"
              onClick={() => setSelectedTab(1)}
            >
              New
            </a>
          </li>
          <li className="nav-item" role="presentation">
            <a
              data-mdb-tab-init
              className={`nav-link ${
                selectedTab === 2 ? "active bg-default text-white" : ""
              }`}
              href="#inProg"
              role="tab"
              aria-controls="opts-tabs-2"
              aria-selected="false"
              onClick={() => setSelectedTab(2)}
            >
              In-Progress
            </a>
          </li>
          <li className="nav-item" role="presentation">
            <a
              data-mdb-tab-init
              className={`nav-link ${
                selectedTab === 3 ? "active bg-default text-white" : ""
              }`}
              href="#past"
              role="tab"
              aria-controls="opts-tabs-3"
              aria-selected="false"
              onClick={() => setSelectedTab(3)}
            >
              Past Orders
            </a>
          </li>
        </ul>
        <div className="tab-content" id="addons-content">
          {selectedTab === 1 && (
            <div
              className="tab-pane fade show active"
              id="new"
              role="tabpanel"
              aria-labelledby="addons-tabs-1"
            >
              <OrderListing
                orders={newOrdersPaginated}
                orderEventsManagedByParent
              />
              <Paginate
                itemsPerPage={itemsPerPage}
                items={newOrders}
                onPaginate={(orders: IOrder[]) => setNewOrdersPaginated(orders)}
              />
            </div>
          )}
          {selectedTab === 2 && (
            <div
              className="tab-pane fade show active"
              id="inProg"
              role="tabpanel"
              aria-labelledby="opts-tab-2"
            >
              <OrderListing
                orders={inProgOrdersPaginated}
                orderEventsManagedByParent
              />
              <Paginate
                itemsPerPage={itemsPerPage}
                items={inProgOrders}
                onPaginate={(orders: IOrder[]) =>
                  setInProgOrdersPaginated(orders)
                }
              />
            </div>
          )}
          {selectedTab === 3 && (
            <div
              className="tab-pane fade show active"
              id="past"
              role="tabpanel"
              aria-labelledby="opts-tab-3"
            >
              <OrderListing
                orders={pastOrdersPaginated}
                orderEventsManagedByParent
              />
              <Paginate
                itemsPerPage={itemsPerPage}
                items={pastOrders}
                onPaginate={(orders: IOrder[]) =>
                  setPastOrdersPaginated(orders)
                }
              />
            </div>
          )}
        </div>
      </div>
      {/* Off-screen (not display:none - react-to-print clones this node, so it
          needs real layout) receipt used purely to drive auto-print. */}
      <div style={{ position: "fixed", top: 0, left: "-9999px" }}>
        <div ref={printableOrderRef}>
          {printableOrder && <OrderReceipt order={printableOrder} />}
        </div>
      </div>
    </div>
  );
});

export default ManageOrders;
